import type { Map as MlMap } from "maplibre-gl";

/**
 * Copernicus CEMS GFM (Global Flood Monitoring) observed-flood-extent overlay —
 * Sentinel-1 SAR. SAR sees THROUGH CLOUD, which the optical NASA MODIS/VIIRS layer
 * cannot: during the monsoon peak cloud hides the surface exactly when flooding is
 * worst, so "no colour ≠ safe" on the optical layer. GFM fills that blind spot.
 *
 * Tiles come from our backend proxy (/api/gfm/tile/{z}/{x}/{y}) — the EODC OAuth2
 * token stays server-side, same arrangement as the GISTDA proxy. The proxy returns
 * 503/transparent until the owner plugs in EODC credentials (ENABLE_GFM_SAR in
 * config.ts gates the UI control in parallel), so this draws nothing until then.
 */
const SOURCE = "gfm-sar-src";
const LAYER = "gfm-sar";
const NATIVE_MAX_ZOOM = 14; // Sentinel-1 ~20 m; cap over-zoom so we don't request absurd tiles
const ATTRIB =
  'น้ำท่วม (เรดาร์ Sentinel-1): <a href="https://global-flood.emergency.copernicus.eu/technical-information/glofas-gfm/" target="_blank" rel="noopener">Copernicus CEMS Global Flood Monitoring (GFM)</a> — contains modified Copernicus data';

let added = false;

/** First label (symbol) layer, so the raster sits beneath road/place names (same as GISTDA/NASA). */
function firstSymbolLayerId(map: MlMap): string | undefined {
  for (const l of map.getStyle().layers ?? []) if (l.type === "symbol") return l.id;
  return undefined;
}

function ensure(map: MlMap): void {
  if (added) return;
  if (!map.getSource(SOURCE)) {
    map.addSource(SOURCE, {
      type: "raster",
      tiles: [`${location.origin}/api/gfm/tile/{z}/{x}/{y}`],
      tileSize: 256,
      maxzoom: NATIVE_MAX_ZOOM,
      attribution: ATTRIB,
    });
  }
  const before = firstSymbolLayerId(map) ?? (map.getLayer("rivers-glow") ? "rivers-glow" : undefined);
  map.addLayer({
    id: LAYER, type: "raster", source: SOURCE, layout: { visibility: "none" },
    // keep GFM's own flood rendering; 'nearest' shows SAR pixels as-is (no invented interpolation)
    paint: { "raster-opacity": 0.8, "raster-resampling": "nearest" },
  }, before);
  added = true;
}

export function addSarFloodLayer(map: MlMap): void { ensure(map); }

/** Show / hide the GFM SAR flood overlay. */
export function setSarFloodVisible(map: MlMap, on: boolean): void {
  ensure(map);
  if (map.getLayer(LAYER)) map.setLayoutProperty(LAYER, "visibility", on ? "visible" : "none");
}
